import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { selectUser } from "../../redux/slices/userSlice";
import { getUserRoles, hasRole } from "../../utils/roles";

const API = "https://api.sedihisham.com";

// Fields of the job application form on the website. Full name, email,
// specialty and CV are always asked. Saved with the website show/hide
// switches as "job_field_<key>".
const FIELDS = [
  { key: "birthday", ar: "تاريخ الميلاد", en: "Birthday" },
  { key: "family_status", ar: "الحالة الاجتماعية", en: "Marital status" },
  { key: "service", ar: "الخدمة الإلزامية", en: "Military service" },
  { key: "sex", ar: "الجنس", en: "Sex" },
  { key: "nationality", ar: "الجنسية", en: "Nationality" },
  { key: "birth_place", ar: "مكان الولادة", en: "Birth place" },
  { key: "landline", ar: "الهاتف الأرضي", en: "Landline" },
  { key: "national_id", ar: "رقم البطاقة الشخصية", en: "National ID" },
  { key: "address", ar: "العنوان", en: "Address" },
  { key: "notes", ar: "ملاحظات", en: "Notes" },
];

function ApplicationFields() {
  const currentUser = useSelector(selectUser);
  const disabled = !hasRole(getUserRoles(currentUser), ["HR", "Admin"]);
  const [switches, setSwitches] = useState(null);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/pages/home/sections`)
      .then((res) => setSwitches(res.data && typeof res.data === "object" ? res.data : {}))
      .catch(() => {
        setSwitches({});
        setError("Could not load the settings — the server may need the latest update.");
      });
  }, []);

  const visible = (key) => switches?.[`job_field_${key}`] !== false;

  const toggle = async (key) => {
    const name = `job_field_${key}`;
    setSavingKey(key);
    setError(null);
    try {
      const res = await axios.post(`${API}/pages/home/sections`, {
        sections: { [name]: !visible(key) },
      });
      setSwitches(res.data && typeof res.data === "object" ? res.data : { ...switches, [name]: !visible(key) });
    } catch (e) {
      setError("Could not save, please try again.");
    }
    setSavingKey(null);
  };

  return (
    <div className="w-[95%] pb-10">
      <p className="text-[15px] font-[700] text-[#033362]">Job application form fields</p>
      <p className="text-[12px] font-[400] text-[#707070] mb-4">
        Click a field to hide it from the application form on the website, click again to
        show it. Full name, email, specialty and CV are always asked. Changes are saved
        right away.
      </p>
      {switches === null ? (
        <p className="text-[14px] font-[400]">Loading...</p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {FIELDS.map((field) => {
            const on = visible(field.key);
            return (
              <button
                key={field.key}
                type="button"
                disabled={disabled || savingKey !== null}
                onClick={() => toggle(field.key)}
                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-[13px] font-[400] ${
                  on ? "border-[#0388CC] bg-[#E9F6FD] text-[#033362]" : "border-[#c4c8cf] bg-[#f5f5f5] text-[#9a9a9a]"
                } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
              >
                <span className="text-left">
                  <span dir="rtl">{field.ar}</span> — {field.en}
                </span>
                <span className={on ? "text-[#007530]" : "text-[#cc4125]"}>
                  <FontAwesomeIcon icon={on ? faEye : faEyeSlash} /> {on ? "Shown" : "Hidden"}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {error ? <p className="text-[14px] font-[400] mt-3 text-[red]">{error}</p> : null}
    </div>
  );
}

export default ApplicationFields;
